<template>
  <div class="d3-chart-wrapper" style="position: relative;">
    <div ref="chartContainer" class="chart-container"></div>
    <div ref="tooltip" class="d3-tooltip" style="opacity: 0; pointer-events: none; position: absolute; background: rgba(0, 0, 0, 0.8); color: #fff; padding: 8px; border-radius: 4px; font-size: 12px; z-index: 10;"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import moment from 'moment';

export default {
  name: 'AuxiliaryEngineUsage',
  props: ['series'],
  data() {
    return {
      resizeObserver: null,
      seriesVisibility: {},
    };
  },
  mounted() {
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    series: { deep: true, handler: 'drawChart' },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;
          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }
            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;
              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },

    processData() {
      if (!this.series) return null;

      const mapData = (path) => this.series.map((data) => {
        const value = path.split('.').reduce((o, i) => (o ? o[i] : undefined), data);
        return Number.isFinite(Number(value)) ? Number(Number(value).toFixed(2)) : null;
      });

      const MappedAe1Fuel = mapData('aeData.AE1.fuelConsumption');
      const MappedAe2Fuel = mapData('aeData.AE2.fuelConsumption');
      const MappedAe3Fuel = mapData('aeData.AE3.fuelConsumption');
      const MappedAe1Sec = mapData('aeData.AE1.runningSeconds');
      const MappedAe2Sec = mapData('aeData.AE2.runningSeconds');
      const MappedAe3Sec = mapData('aeData.AE3.runningSeconds');

      const timestamps = this.series.map((d) => new Date(d.timestamp * 1000));

      const datasets = [
        {
          name: 'AE 1 Fuel (L)', data: MappedAe1Fuel, interpolated: this.interpolateNulls(MappedAe1Fuel), color: '#FF0000', axis: 'left',
        },
        {
          name: 'AE 2 Fuel (L)', data: MappedAe2Fuel, interpolated: this.interpolateNulls(MappedAe2Fuel), color: '#00FF00', axis: 'left',
        },
        {
          name: 'AE 3 Fuel (L)', data: MappedAe3Fuel, interpolated: this.interpolateNulls(MappedAe3Fuel), color: '#0000FF', axis: 'left',
        },
        {
          name: 'AE 1 Run (s)', data: MappedAe1Sec, interpolated: this.interpolateNulls(MappedAe1Sec), color: '#FFFF00', axis: 'right',
        },
        {
          name: 'AE 2 Run (s)', data: MappedAe2Sec, interpolated: this.interpolateNulls(MappedAe2Sec), color: '#FF00FF', axis: 'right',
        },
        {
          name: 'AE 3 Run (s)', data: MappedAe3Sec, interpolated: this.interpolateNulls(MappedAe3Sec), color: '#00FFFF', axis: 'right',
        },
      ];

      return { timestamps, datasets };
    },

    drawChart() {
      const processed = this.processData();
      if (!processed) return;
      const { timestamps, datasets } = processed;
      const container = this.$refs.chartContainer;

      d3.select(container).selectAll('*').remove();

      const visibility = this.seriesVisibility || {};
      datasets.forEach((dataset) => {
        if (visibility[dataset.name] === undefined) visibility[dataset.name] = true;
      });
      this.seriesVisibility = visibility;

      const margin = {
        top: 20, right: 60, bottom: 50, left: 60,
      };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 600 - margin.top - margin.bottom;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // X Axis
      const x = d3.scaleTime().domain(d3.extent(timestamps)).range([0, width]);
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat((d) => moment(d).format('DD/MM HH:mm')))
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');

      // Y Axis Left (Fuel)
      const maxFuel = d3.max(datasets.filter((d) => d.axis === 'left').flatMap((d) => d.interpolated)) || 10;
      const yLeft = d3.scaleLinear().domain([0, maxFuel * 1.1]).range([height, 0]);
      svg.append('g').call(d3.axisLeft(yLeft))
        .append('text').attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', -40)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('Fuel Consumption (L)');

      // Y Axis Right (Seconds)
      const maxSec = d3.max(datasets.filter((d) => d.axis === 'right').flatMap((d) => d.interpolated)) || 10;
      const yRight = d3.scaleLinear().domain([0, maxSec * 1.1]).range([height, 0]);
      svg.append('g').attr('transform', `translate(${width}, 0)`).call(d3.axisRight(yRight))
        .append('text')
        .attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', 40)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('Running Seconds');

      // Grid
      svg.append('g').attr('class', 'grid').attr('opacity', 0.1).call(d3.axisLeft(yLeft).tickSize(-width).tickFormat(''));

      // Draw Lines & Dots
      const seriesElements = {};
      const realLineWidth = 2.5;
      const hoverLineWidth = 4;
      const interpolatedLineWidth = 1.5;
      datasets.forEach((series, index) => {
        const yScale = series.axis === 'left' ? yLeft : yRight;

        // Line Interpolated
        const interpolatedLine = svg.append('path')
          .datum(series.interpolated)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', interpolatedLineWidth)
          .attr('stroke-dasharray', '4 4')
          .attr('opacity', 0.6)
          .attr('d', d3.line().x((d, i) => x(timestamps[i])).y((d) => yScale(d)));

        // Line Real
        const realLine = svg.append('path')
          .datum(series.data)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', realLineWidth)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)))
          .on('mouseover', function handleMouseOver() {
            d3.select(this).attr('stroke-width', hoverLineWidth);
          })
          .on('mouseout', function handleMouseOut() {
            d3.select(this).attr('stroke-width', realLineWidth);
          });

        // Dots for Real Data
        const dots = svg.selectAll(`.dot-series-${index}`)
          .data(series.data.map((d, i) => ({ value: d, date: timestamps[i] })).filter((d) => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', `dot-series-${index}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');

        seriesElements[series.name] = {
          interpolatedLine,
          realLine,
          dots,
        };
      });

      // Tooltip & Hover
      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;

      const focusGroup = svg.append('g').style('display', 'none');

      const focusLine = focusGroup.append('line')
        .attr('stroke', '#aaa').attr('stroke-dasharray', '3 3').attr('y1', 0)
        .attr('y2', height);

      const focusCircles = datasets.map((ds) => focusGroup.append('circle').attr('r', 5).attr('fill', ds.color).attr('stroke', '#fff')
        .attr('stroke-width', 2));

      const updateSeriesVisibility = () => {
        datasets.forEach((ds, index) => {
          const isVisible = visibility[ds.name];
          const elements = seriesElements[ds.name];
          elements.interpolatedLine.style('display', isVisible ? null : 'none');
          elements.realLine.style('display', isVisible ? null : 'none');
          elements.dots.style('display', isVisible ? null : 'none');
          focusCircles[index].style('display', isVisible ? null : 'none');
        });
      };

      const legendSpacing = 18;
      const legendItemWidth = 160;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendWidth = Math.min(datasets.length, legendColumns) * legendItemWidth;
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - legendWidth) / 2)}, -10)`);
      const legendItem = legendGroup.selectAll('g')
        .data(datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * legendSpacing})`;
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          visibility[d.name] = !visibility[d.name];
          updateSeriesVisibility();
          // eslint-disable-next-line no-use-before-define
          updateLegend();
        });

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '12px')
        .text((d) => d.name);

      const updateLegend = () => {
        legendItem.select('rect').attr('opacity', (d) => (visibility[d.name] ? 1 : 0.3));
        legendItem.select('text')
          .attr('opacity', (d) => (visibility[d.name] ? 1 : 0.4))
          .style('text-decoration', (d) => (visibility[d.name] ? 'none' : 'line-through'));
      };

      updateSeriesVisibility();
      updateLegend();

      svg.append('rect')
        .attr('width', width).attr('height', height).style('fill', 'none')
        .style('pointer-events', 'all')
        .on('mouseover', () => { tooltip.style('opacity', 1); focusGroup.style('display', null); })
        .on('mouseout', () => { tooltip.style('opacity', 0); focusGroup.style('display', 'none'); })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          // eslint-disable-next-line no-nested-ternary
          const index = i < timestamps.length && i > 0 ? (x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1) : 0;
          const date = timestamps[index];

          focusLine.attr('x1', x(date)).attr('x2', x(date));

          datasets.forEach((ds, i) => {
            if (!visibility[ds.name]) {
              focusCircles[i].style('display', 'none');
              return;
            }
            const val = ds.data[index];
            const yScale = ds.axis === 'left' ? yLeft : yRight;
            if (val !== null && val !== undefined) {
              focusCircles[i].attr('cx', x(date)).attr('cy', yScale(val)).style('display', null);
            } else {
              focusCircles[i].style('display', 'none');
            }
          });

          let html = `<strong>${moment(date).format('DD MMM YYYY HH:mm')}</strong><br/>`;
          datasets.forEach((ds) => {
            if (!visibility[ds.name]) return;
            const val = ds.data[index];
            if (val !== null && val !== undefined) {
              html += `<div style="display:flex; align-items:center; gap:5px;">
                          <span style="width:10px; height:10px; border-radius:50%; background:${ds.color}; display:inline-block;"></span>
                          ${ds.name}: ${val}
                        </div>`;
            }
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          const top = posY + 15;
          if (left + 180 > width) left = posX - 190;

          tooltip.html(html).style('left', `${left}px`).style('top', `${top}px`);
        });
    },
  },
};
</script>

<style scoped>
.d3-chart-wrapper { font-family: Helvetica, Arial, sans-serif; background: #fff; }
.chart-container { width: 100%; }
</style>
